import React, { useMemo, useState } from 'react';
import {
  BadgeCheck,
  CheckCircle2,
  Clock,
  BookOpen,
  Award,
  TrendingUp,
  Play,
  ShoppingCart,
  ChevronRight,
} from 'lucide-react';
import SvgCourseThumbnail from './SvgCourseThumbnail';
import { Course, Instructor } from '../../types';
import { courseWeeksLabel, describeCourseTiming } from '../../lib/courseTiming';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Img, accentFor, cn } from '../ui';
import { useAuthUser, useEnrolledCourses } from '../../store/user';
import { useSnackbar } from '../../context/SnackbarContext';
import { useCartStore } from '../../store/cart';
import { getInstructorById } from '../../apis/instructor';
import InstructorProfileModal from '../InstructorProfileModal';
import { effectivePriceNgn, strikethroughPriceNgn } from '../../lib/coursePricing';
import { enrollmentCourseId, isEnrolledIn } from '../../lib/enrollment';

interface CourseCardProps {
  course: Course;
  enrollmentType?: 'paid' | 'scholarship' | 'free' | null;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  enrollmentType = null,
}) => {
  const navigate = useNavigate();
  // Selectors, not the whole store: a catalogue holds sixty of these cards, and
  // subscribing to every slice re-rendered all of them on any unrelated change.
  const enrolledCourses = useEnrolledCourses();
  const authUser = useAuthUser();
  const { showSnackbar } = useSnackbar();
  const { addItem } = useCartStore();

  const [isInstructorModalOpen, setIsInstructorModalOpen] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);

  /**
   * Whether this learner already has the course, read off the enrolment list the
   * store already holds.
   *
   * This used to be an effect that asked the server per card, and then called
   * `refreshEnrolledCourses()` whenever the answer disagreed with the store — a
   * loop, because `loadEnrolledCourses` always publishes a *new* array, which
   * changed this effect's `enrolledCourses` dependency, which ran the check
   * again. A free course made the disagreement permanent: `price_ngn === 0`
   * short-circuited to "has access" while nothing ever enrolled the learner, so
   * the refresh fired forever. On /courses that meant every one of the 61 cards
   * re-requesting `/access` on every turn of the loop — tens of thousands of
   * requests within seconds of scrolling to the bottom, which is what crashed
   * the tab on mobile Safari.
   *
   * The list is the same source the server was consulting, is loaded once by
   * `initializeUser` and refreshed at the points that actually change it
   * (checkout, payment callback, the enrolled page). Free courses need no lookup
   * at all: `isFreeCourse` below already opens them to everyone.
   */
  const isEnrolled = useMemo(
    () => Boolean(authUser?._id) && isEnrolledIn(enrolledCourses, course._id),
    [authUser?._id, enrolledCourses, course._id]
  );

  /**
   * What this learner has already done with the course.
   *
   * Read off the enrolment the store already holds, with no request of its own:
   * a catalogue renders dozens of these cards at once, and the enrolment row
   * carries everything the mark needs. `progress` is a mirror that can lag the
   * progress record, so a `completed` status and an issued certificate each
   * count as finished in their own right.
   */
  const standing = useMemo((): 'certified' | 'completed' | null => {
    const enrollment = Array.isArray(enrolledCourses)
      ? enrolledCourses.find((entry) => enrollmentCourseId(entry) === String(course._id))
      : undefined;

    if (!enrollment) return null;
    if (enrollment.certificateIssued) return 'certified';
    if ((enrollment.progress ?? 0) >= 100 || String(enrollment.status) === 'completed') return 'completed';
    return null;
  }, [enrolledCourses, course._id]);

  const handleInstructorClick = async () => {
    if (course.instructorId) {
      try {
        const instructor = await getInstructorById(course.instructorId);
        setSelectedInstructor(instructor);
        setIsInstructorModalOpen(true);
      } catch (error) {
        console.error('Error fetching instructor details:', error);
        showSnackbar('Could not load instructor profile.', 'error');
      }
    }
  };

  const getLevelIcon = (level?: string) => {
    if (!level || typeof level !== 'string') {
      return <BookOpen className="w-3 h-3" />;
    }
    switch (level.toLowerCase()) {
      case 'beginner':
        return <Play className="w-3 h-3" />;
      case 'intermediate':
        return <TrendingUp className="w-3 h-3" />;
      case 'advanced':
        return <Award className="w-3 h-3" />;
      default:
        return <BookOpen className="w-3 h-3" />;
    }
  };

  const handleAddToCart = (course: Course) => {
    // Determine the actual price to add to cart
    const basePrice = course.price_ngn;

    if (basePrice === undefined || basePrice === null || basePrice <= 0) {
      showSnackbar('Course price is not available.', 'error');
      return;
    }

    const finalPrice = Math.max(0, Math.floor(effectivePriceNgn(course)));

    addItem({
      courseId: course._id,
      quantity: 1,
      price: finalPrice,
      name: course.title,
      image: course.coverImage || undefined,
    });
    showSnackbar(`${course.title} added to cart!`, 'success');
  };

  const isFreeCourse = course.price_ngn === 0 && course.price_usd === 0;

  // Determine display price and strikethrough logic
  const getDisplayPrice = () => {
    const basePrice = course.price_ngn;
    if (basePrice === undefined || basePrice === null || basePrice <= 0) {
      return { final: null, original: null };
    }

    return {
      final: Math.max(0, Math.floor(effectivePriceNgn(course))),
      original: strikethroughPriceNgn(course),
    };
  };

  const { final: displayPrice, original: strikethroughPrice } = getDisplayPrice();

  /**
   * Duration reads as a schedule, not a file size. A course with no duration
   * shows nothing here rather than "N/A" — an empty slot asks no questions.
   */
  const timing = describeCourseTiming(course);
  const weeksLabel = courseWeeksLabel(course);

  const detailsHref = `/course-details/${course.slug || course._id}`;

  // Derived from the category name, so the same subject carries the same colour
  // on the homepage, in the catalogue and on the course page.
  const palette = accentFor(course.categories?.[0]);

  return (
    // The card was a <div onClick>, which keyboard and screen-reader users could not
    // reach. The title is now the real link and it stretches over the card via the
    // ::after overlay, so the whole surface stays clickable while the nested
    // instructor and cart controls sit above it on their own z-index.
    <Card
      interactive
      accentRule={palette.rule}
      className="group flex flex-col h-full focus-within:border-primary/40"
    >
      {(course.featured || enrollmentType === 'scholarship') && (
        <div className="absolute top-3 left-3 z-20 flex flex-col items-start gap-1.5">
          {course.featured && <Badge tone="accent">Featured</Badge>}
          {enrollmentType === 'scholarship' && (
            <Badge tone="success">
              <Award className="w-3 h-3" />
              Scholarship
            </Badge>
          )}
        </div>
      )}

      {/* Solid rather than the tinted Badge used on the left: this is the one
          thing on the card that is about the learner rather than the course, and
          it should be readable at a glance across a grid of them. */}
      {standing && (
        <div className="absolute top-3 right-3 z-20">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 shadow-xs',
              'font-sans text-overline font-semibold uppercase',
              standing === 'certified' ? 'bg-gold text-[#17181C]' : 'bg-success text-white',
            )}
          >
            {standing === 'certified' ? (
              <BadgeCheck className="h-3 w-3" aria-hidden="true" />
            ) : (
              <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
            )}
            {standing === 'certified' ? 'Certified' : 'Completed'}
          </span>
        </div>
      )}

      <div className={cn('relative overflow-hidden w-full aspect-[3/2]', palette.soft)}>
        <Img
          src={course.coverImage}
          width={480}
          height={320}
          className="h-full w-full"
          fallback={<SvgCourseThumbnail title={course.title} width={320} height={200} seed={course._id} />}
        />

      </div>

      <div className="p-5 flex flex-col flex-grow">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            {course.categories.slice(0, 2).map((category, index) => (
              <span
                key={index}
                className={cn(
                  'truncate rounded-sm border px-2 py-0.5 text-overline font-semibold uppercase',
                  accentFor(category).chip,
                )}
              >
                {category}
              </span>
            ))}
            {course.categories.length > 2 && (
              <span className="text-overline font-semibold text-muted-foreground">
                +{course.categories.length - 2}
              </span>
            )}
          </div>
        </div>

        <h3 className="font-sans text-h4 font-semibold text-text-primary mb-2 line-clamp-2">
          <Link
            to={detailsHref}
            className="after:absolute after:inset-0 after:content-[''] hover:text-primary transition-colors"
          >
            {course.title}
          </Link>
        </h3>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleInstructorClick();
          }}
          className="relative z-10 flex items-center gap-2 mb-3 w-fit text-caption text-text-secondary hover:text-primary transition-colors"
        >
          {course.authorAvatar && (
            <img
              src={course.authorAvatar}
              alt=""
              loading="lazy"
              className="w-6 h-6 rounded-full border border-border"
            />
          )}
          <span>{course.instructorName}</span>
        </button>

        {/* `whitespace-nowrap` per item so the row wraps between facts rather
            than splitting "6 modules" across two lines. */}
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-caption text-text-secondary">
          {weeksLabel && (
            <span
              className="flex items-center gap-1.5 whitespace-nowrap"
              /* The content total is not lost, just demoted: it is the answer to
                 a question ("how much is there?") nobody asks before the pace. */
              title={timing ? `${timing.total} of content · ${timing.pace}` : undefined}
            >
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {weeksLabel}
            </span>
          )}
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
            {course.moduleCount ?? course.modules?.length ?? 0} modules
          </span>
          {course.level && (
            <span className="flex items-center gap-1.5 whitespace-nowrap capitalize">
              {getLevelIcon(course.level)}
              {course.level}
            </span>
          )}
        </div>

        {/**
          * No star rating.
          *
          * Every course in the catalogue carries `rating: 5, ratingCount: 100`,
          * and nothing in the application has ever written either field —
          * there is no review form, no rating endpoint, and no course model
          * hook that touches them. They are seed values from
          * scripts/seedCourses.ts, and this card was rendering them as five
          * gold stars and "5.0 (100)" on all sixty courses.
          *
          * That is fabricated social proof on the primary buying surface. It is
          * also self-defeating: an identical perfect score on every course in a
          * catalogue reads as decoration, not as evidence, to anybody who scrolls
          * twice.
          *
          * The block comes back the day a real review exists — the honest
          * version reads from learner-submitted ratings and shows nothing until
          * there are enough of them to mean anything. See
          * marketing/conversion/CONVERSION-AUDIT.md.
          */}

        <div className="flex items-end justify-between gap-3 pt-4 border-t border-border mt-auto">
          <div className="flex flex-col">
            {isFreeCourse ? (
              <span className="font-display text-h3 text-text-primary">Free</span>
            ) : displayPrice === null ? (
              <span className="text-caption text-muted-foreground">Price unavailable</span>
            ) : (
              <>
                {strikethroughPrice !== null && (
                  <span className="text-caption text-muted-foreground line-through">
                    ₦{strikethroughPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                )}
                <span className="font-display text-h3 text-text-primary">
                  ₦{displayPrice.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </>
            )}
          </div>

          <div className="relative z-10 shrink-0">
            {(isEnrolled || enrollmentType === 'scholarship' || isFreeCourse) ? (
              <Button
                size="sm"
                trailingIcon={<ChevronRight className="w-4 h-4" />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/course/${course.slug || course._id}`);
                }}
              >
                {standing ? 'Review' : 'Continue'}
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                aria-label={`Add ${course.title} to cart`}
                leadingIcon={<ShoppingCart className="w-4 h-4" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(course);
                }}
              >
                Add
              </Button>
            )}
          </div>
        </div>
      </div>

      <InstructorProfileModal
        isOpen={isInstructorModalOpen}
        onClose={() => setIsInstructorModalOpen(false)}
        instructor={selectedInstructor}
      />
    </Card>
  );
};

export default CourseCard;